// Cross-platform storage service that works in normal and incognito modes
// Provides fallback mechanisms for when localStorage is unavailable

interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

class MemoryStorage implements StorageInterface {
  private storage: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

class SafeStorageService {
  private storage!: StorageInterface; // Initialized in constructor via initializeStorage()
  private isLocalStorageAvailable: boolean = false;
  private isSessionStorageAvailable: boolean = false;
  private memoryFallback: MemoryStorage;

  constructor() {
    this.memoryFallback = new MemoryStorage();
    this.detectStorageAvailability();
    this.initializeStorage();
  }

  private detectStorageAvailability(): void {
    // Test localStorage availability
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      this.isLocalStorageAvailable = true;
      console.log('✅ localStorage available');
    } catch (error) {
      console.warn('⚠️ localStorage not available (likely incognito mode):', error);
      this.isLocalStorageAvailable = false;
    }

    // Test sessionStorage availability
    try {
      const testKey = '__session_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      this.isSessionStorageAvailable = true;
      console.log('✅ sessionStorage available');
    } catch (error) {
      console.warn('⚠️ sessionStorage not available:', error);
      this.isSessionStorageAvailable = false;
    }
  }

  private initializeStorage(): void {
    if (this.isLocalStorageAvailable) {
      this.storage = localStorage;
      console.log('📦 Using localStorage for persistence');
    } else if (this.isSessionStorageAvailable) {
      this.storage = sessionStorage;
      console.log('📦 Using sessionStorage for persistence (incognito mode)');
    } else {
      this.storage = this.memoryFallback;
      console.log('📦 Using memory storage fallback (limited persistence)');
    }
  }

  // Public API methods
  getItem(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      console.error('Error getting item from storage:', error);
      return this.memoryFallback.getItem(key);
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      console.error('Error setting item in storage:', error);
      // Fallback to memory storage
      this.memoryFallback.setItem(key, value);
    }
  }

  removeItem(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error('Error removing item from storage:', error);
      this.memoryFallback.removeItem(key);
    }
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
      this.memoryFallback.clear();
    }
  }

  // Utility methods
  getStorageType(): 'localStorage' | 'sessionStorage' | 'memory' {
    if (this.isLocalStorageAvailable) return 'localStorage';
    if (this.isSessionStorageAvailable) return 'sessionStorage';
    return 'memory';
  }

  isPersistent(): boolean {
    return this.isLocalStorageAvailable;
  }

  isIncognitoMode(): boolean {
    return !this.isLocalStorageAvailable && this.isSessionStorageAvailable;
  }

  // JSON helpers
  getJSON<T>(key: string, defaultValue: T): T {
    try {
      const item = this.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error parsing JSON from storage:', error);
      return defaultValue;
    }
  }

  setJSON(key: string, value: any): void {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error stringifying JSON for storage:', error);
    }
  }
}

// Export singleton instance
export const storageService = new SafeStorageService();

// Export types for use in other modules
export type { StorageInterface };
export { SafeStorageService };
